import { useMemo, useState } from "react";
import { Navigate, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { ArrowLeft, Clock, MessageSquare, User } from "lucide-react";
import { Button } from "@jourfx/ui";
import { ClientLayout } from "../components/layout/ClientLayout";
import { useSession } from "../hooks/useSession";
import { useVersichertenSession } from "../hooks/useVersichertenSession";
import { DateTimeSlotPicker } from "../components/appointment/DateTimeSlotPicker";

type Id<T extends string> = string & { __tableName: T };

export function BookingPage() {
  const { invitationId } = useParams({ from: "/booking/$invitationId" });
  const search = useSearch({ from: "/booking/$invitationId" });
  const navigate = useNavigate();
  const { createSession: createAppointmentSession } = useSession();
  const {
    session: versichertenSession,
    isValid: hasVersichertenSession,
    isLoading: versichertenSessionLoading,
  } = useVersichertenSession();
  const apiRef = api as any;
  const searchParams = search as { code?: string };
  const normalizedAccessCode =
    typeof searchParams.code === "string"
      ? searchParams.code.trim().toUpperCase()
      : "";

  const effectiveSessionToken = versichertenSession?.sessionToken || "";

  const [selectedDate, setSelectedDate] = useState(() => {
    const next = new Date();
    next.setDate(next.getDate() + 1);
    next.setHours(0, 0, 0, 0);
    return next;
  });
  const selectedDateOffsetMinutes = useMemo(
    () => selectedDate.getTimezoneOffset(),
    [selectedDate]
  );
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [callbackNote, setCallbackNote] = useState("");
  const [callbackRequested, setCallbackRequested] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [isRequestingCallback, setIsRequestingCallback] = useState(false);
  const [bookingResult, setBookingResult] = useState<{
    appointmentId: string;
    emailSent: boolean;
  } | null>(null);

  const bookingInfo = useQuery(
    apiRef.consultationInvitations.index.getForBooking,
    effectiveSessionToken
      ? {
          invitationId: invitationId as Id<"consultationInvitations">,
          sessionToken: effectiveSessionToken,
        }
      : "skip"
  );

  const slots = useQuery(
    apiRef.consultationInvitations.index.getAvailableSlots,
    effectiveSessionToken
      ? {
          invitationId: invitationId as Id<"consultationInvitations">,
          sessionToken: effectiveSessionToken,
          date: selectedDate.getTime(),
          tzOffsetMinutes: selectedDateOffsetMinutes,
        }
      : "skip"
  ) as Array<{ time: string; timestamp: number; available: boolean }> | undefined;

  const bookSlot = useMutation(apiRef.consultationInvitations.index.bookSlot);
  const requestCallback = useMutation(apiRef.consultationInvitations.index.requestCallback);

  const availableSlots = useMemo(
    () => (slots || []).filter((slot) => slot.available),
    [slots]
  );

  if (versichertenSessionLoading) {
    return (
      <ClientLayout>
        <div className="client-card w-full max-w-md p-8 text-center">
          <div className="client-spinner mx-auto mb-4" aria-hidden="true" />
          <p className="text-client-muted font-medium">Lade Benutzersession...</p>
        </div>
      </ClientLayout>
    );
  }

  if (!hasVersichertenSession) {
    return (
      <Navigate
        to="/"
        search={
          {
            ...(normalizedAccessCode ? { code: normalizedAccessCode } : {}),
            next: `/booking/${invitationId}`,
          } as any
        }
      />
    );
  }

  if (!effectiveSessionToken || bookingInfo === undefined) {
    return (
      <ClientLayout>
        <div className="client-card w-full max-w-md p-8 text-center">
          <div className="client-spinner mx-auto mb-4" aria-hidden="true" />
          <p className="text-client-muted font-medium">Lade Buchungsdaten...</p>
        </div>
      </ClientLayout>
    );
  }

  if (!bookingInfo) {
    return (
      <ClientLayout>
        <div className="client-card w-full max-w-md p-8 text-center space-y-4">
          <p className="text-client-muted font-medium">
            Diese Einladung ist nicht mehr verfügbar.
          </p>
          <Button variant="secondary" size="client" className="w-full" onClick={() => navigate({ to: "/" })}>
            Zur Startseite
          </Button>
        </div>
      </ClientLayout>
    );
  }

  const handleDateChange = (date: Date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    setSelectedDate(next);
    setSelectedSlot(null);
  };

  const handleBook = async () => {
    if (!selectedSlot) return;
    setIsBooking(true);
    try {
      const result = await bookSlot({
        invitationId: invitationId as Id<"consultationInvitations">,
        sessionToken: effectiveSessionToken,
        scheduledAt: selectedSlot,
      });
      createAppointmentSession(result.appointmentId, result.sessionExpiresAt, result.sessionToken);
      if (hasVersichertenSession) {
        navigate({ to: "/dashboard" } as any);
        return;
      }
      setBookingResult({
        appointmentId: result.appointmentId,
        emailSent: result.emailSent,
      });
    } catch (error) {
      console.error("Booking failed", error);
    } finally {
      setIsBooking(false);
    }
  };

  const handleRequestCallback = async () => {
    setIsRequestingCallback(true);
    try {
      await requestCallback({
        invitationId: invitationId as Id<"consultationInvitations">,
        sessionToken: effectiveSessionToken,
        preferredDateStart: selectedDate.getTime(),
        preferredDateEnd: selectedDate.getTime() + 24 * 60 * 60 * 1000 - 1,
        notes: callbackNote.trim() || undefined,
      });
      setCallbackRequested(true);
    } catch (error) {
      console.error("Callback request failed", error);
    } finally {
      setIsRequestingCallback(false);
    }
  };

  if (bookingResult) {
    return (
      <ClientLayout>
        <div className="client-card w-full max-w-md p-6 space-y-4">
          <h1 className="text-2xl font-bold text-[var(--stone-800)]">Termin gebucht</h1>
          <p className="text-[var(--stone-600)]">
            Ihr Termin wurde erfolgreich erstellt.
          </p>
          <div className="rounded-lg border border-[var(--stone-200)] bg-[var(--stone-50)] p-3 text-sm text-[var(--stone-700)]">
            {bookingResult.emailSent
              ? "Eine Bestätigungs-E-Mail wurde an Sie versendet."
              : "Es konnte keine E-Mail versendet werden, da keine E-Mail-Adresse hinterlegt ist."}
          </div>
          <Button
            size="client"
            className="w-full"
            onClick={() =>
              navigate({
                to: "/appointment/$appointmentId",
                params: { appointmentId: bookingResult.appointmentId },
              })
            }
          >
            Zum Termin
          </Button>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="client-card w-full max-w-lg p-6 space-y-6">
        {hasVersichertenSession && (
          <button
            onClick={() => navigate({ to: "/dashboard" } as any)}
            className="flex items-center gap-1 text-sm text-[var(--stone-600)] hover:text-[var(--sage-600)] transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Übersicht
          </button>
        )}
        <div className="space-y-3">
          <h1 className="text-2xl font-bold text-[var(--stone-800)]">Termin buchen</h1>
          <p className="text-[var(--stone-600)]">
            Bitte wählen Sie einen freien Termin bei Ihrem zugewiesenen Gutachter.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-[var(--stone-200)] p-4 bg-[var(--stone-50)]">
            <div className="flex items-center gap-2 text-[var(--stone-500)] text-sm mb-1">
              <User className="h-4 w-4" />
              Gutachter
            </div>
            <p className="font-semibold text-[var(--stone-800)]">
              {bookingInfo.gutachter?.name || "Nicht zugewiesen"}
            </p>
          </div>
          <div className="rounded-xl border border-[var(--stone-200)] p-4 bg-[var(--stone-50)]">
            <div className="flex items-center gap-2 text-[var(--stone-500)] text-sm mb-1">
              <Clock className="h-4 w-4" />
              Dauer
            </div>
            <p className="font-semibold text-[var(--stone-800)]">
              {bookingInfo.invitation.duration} Minuten
            </p>
          </div>
        </div>

        <DateTimeSlotPicker
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          slots={slots || []}
          isLoading={slots === undefined || isBooking}
          selectedSlot={selectedSlot}
          onSlotSelect={setSelectedSlot}
          onConfirm={handleBook}
          confirmLabel="Termin verbindlich buchen"
          busyLabel="Lade verfügbare Zeiten..."
          emptyLabel="Für diesen Tag sind keine freien Termine verfügbar."
          className=""
          slotGridClassName="sm:grid-cols-4"
        />

        {(callbackRequested || bookingInfo.callbackRequested || (slots && availableSlots.length === 0)) && (
          <div className="space-y-3 rounded-xl border border-[var(--stone-200)] p-4">
            <h3 className="flex items-center gap-2 font-semibold text-[var(--stone-700)]">
              <MessageSquare className="h-4 w-4" />
              Rückrufwunsch
            </h3>
            {callbackRequested || bookingInfo.callbackRequested ? (
              <p className="text-sm text-[var(--stone-600)]">
                Ihr Rückrufwunsch wurde gespeichert. Das Team meldet sich bei Ihnen.
              </p>
            ) : (
              <>
                <p className="text-sm text-[var(--stone-600)]">
                  Wenn kein Termin passt, können Sie einen Rückruf anfordern.
                </p>
                <textarea
                  value={callbackNote}
                  onChange={(event) => setCallbackNote(event.target.value)}
                  placeholder="Optional: Wunschzeit oder Hinweis"
                  className="min-h-20 w-full rounded-lg border border-[var(--stone-200)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sage-200)]"
                />
                <Button
                  variant="secondary"
                  size="client"
                  className="w-full"
                  onClick={handleRequestCallback}
                  disabled={isRequestingCallback}
                >
                  Rückruf anfragen
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
